/**
 * Patient Registration Validation Hook
 *
 * Provides comprehensive validation functions for the patient registration workflow.
 * Based on the existing dietician validation patterns and patient-specific requirements.
 *
 * @module hooks/usePatientValidation
 */

import { useCallback } from 'react';
import type {
  PatientRegistrationData,
  PatientAddressData,
  PatientPhoneData,
  UsePatientValidationReturn
} from '../types/patientRegistration';
import type { ValidationResult } from '../types/registration';
import { validateEmail as validateEmailUtil } from '../utils/validation/emailValidation';
import { validatePassword as validatePasswordUtil } from '../utils/validation/passwordValidation';

// Import Australian states from centralized constants
import { AUSTRALIAN_STATES } from '../constants/geographic';

export function usePatientValidation(): UsePatientValidationReturn {
  // Email validation
  const validateEmail = useCallback((email: string): ValidationResult => {
    if (email.length === 0) {
      return { isValid: false, error: 'Email is required' };
    }

    const emailResult = validateEmailUtil(email);
    if (!emailResult.isValid) {
      return emailResult;
    }

    return { isValid: true };
  }, []);

  // Password validation (reuse existing logic)
  const validatePassword = useCallback((password: string): ValidationResult => {
    return validatePasswordUtil(password);
  }, []);

  // Name validation
  const validateName = useCallback((name: string, fieldName: string): ValidationResult => {
    if (name.length === 0) {
      return { isValid: false, error: `${fieldName} is required` };
    }

    if (name.length < 2) {
      return { isValid: false, error: `${fieldName} must be at least 2 characters` };
    }

    if (name.length > 50) {
      return { isValid: false, error: `${fieldName} must be less than 50 characters` };
    }

    const namePattern = /^[a-zA-Z\s\-']+$/;
    if (!namePattern.test(name)) {
      return { isValid: false, error: `${fieldName} contains invalid characters` };
    }

    return { isValid: true };
  }, []);

  // Street address validation
  const validateStreet = useCallback((street: string): ValidationResult => {
    if (!street.trim()) {
      return { isValid: false, error: 'Street address is required' };
    }

    if (street.length < 5) {
      return { isValid: false, error: 'Street address must be at least 5 characters' };
    }

    if (street.length > 100) {
      return { isValid: false, error: 'Street address must be less than 100 characters' };
    }

    return { isValid: true };
  }, []);

  // City validation
  const validateCity = useCallback((city: string): ValidationResult => {
    if (!city.trim()) {
      return { isValid: false, error: 'City is required' };
    }

    if (city.length < 2) {
      return { isValid: false, error: 'City must be at least 2 characters' };
    }

    if (city.length > 50) {
      return { isValid: false, error: 'City must be less than 50 characters' };
    }

    return { isValid: true };
  }, []);

  // Australian state validation
  const validateState = useCallback((state: string): ValidationResult => {
    if (state.length === 0) {
      return { isValid: false, error: 'State is required' };
    }

    if (!AUSTRALIAN_STATES.includes(state)) {
      return { isValid: false, error: 'Please select a valid state or territory' };
    }

    return { isValid: true };
  }, []);

  // Postal code validation (Australian format)
  const validatePostalCode = useCallback((postalCode: string): ValidationResult => {
    if (!postalCode.trim()) {
      return { isValid: false, error: 'Postcode is required' };
    }

    const australianPostcodeRegex = /^[0-9]{4}$/;
    if (!australianPostcodeRegex.test(postalCode)) {
      return { isValid: false, error: 'Please enter a valid 4-digit postcode' };
    }

    return { isValid: true };
  }, []);

  // Address validation
  const validateAddress = useCallback((address: PatientAddressData): ValidationResult => {
    const streetResult = validateStreet(address.street);
    if (!streetResult.isValid) {
      return streetResult;
    }

    const cityResult = validateCity(address.city);
    if (!cityResult.isValid) {
      return cityResult;
    }

    const stateResult = validateState(address.state);
    if (!stateResult.isValid) {
      return stateResult;
    }

    const postalCodeResult = validatePostalCode(address.postal_code);
    if (!postalCodeResult.isValid) {
      return postalCodeResult;
    }

    return { isValid: true };
  }, [validateStreet, validateCity, validateState, validatePostalCode]);

  // Phone validation
  const validatePhone = useCallback((phoneData: PatientPhoneData): ValidationResult => {
    if (!phoneData.number.trim()) {
      return { isValid: false, error: 'Phone number is required' };
    }

    // Australian phone number validation - format: 0X XXXX XXXX where first X cannot be 0
    const australianPhoneRegex = /^0[1-9][0-9]{8}$/;
    const cleanNumber = phoneData.number.replace(/\s/g, '');

    if (!australianPhoneRegex.test(cleanNumber)) {
      return { isValid: false, error: 'Please enter a valid Australian phone number (0X XXXX XXXX where first X cannot be 0)' };
    }

    return { isValid: true };
  }, []);

  // Date of birth validation
  const validateDateOfBirth = useCallback((dateString: string): ValidationResult => {
    if (!dateString) {
      return { isValid: false, error: 'Date of birth is required' };
    }

    const date = new Date(dateString);
    const today = new Date();

    if (isNaN(date.getTime())) {
      return { isValid: false, error: 'Please enter a valid date' };
    }

    if (date > today) {
      return { isValid: false, error: 'Date of birth cannot be in the future' };
    }

    // Check minimum age (13 years)
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 13);

    if (date > minDate) {
      return { isValid: false, error: 'You must be at least 13 years old to register' };
    }

    return { isValid: true };
  }, []);

  // Photo validation
  const validatePhoto = useCallback((file: File): ValidationResult => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

    if (file.size > maxSize) {
      return { isValid: false, error: 'Photo must be less than 5MB' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Photo must be JPEG or PNG format' };
    }

    return { isValid: true };
  }, []);

  // Registration step validation (Step 1)
  const validateRegistrationStep = useCallback((data: Partial<PatientRegistrationData>): ValidationResult => {
    const errors: string[] = [];

    const emailResult = validateEmail(data.email || '');
    if (!emailResult.isValid) {
      errors.push(emailResult.error!);
    }

    const passwordResult = validatePassword(data.password || '');
    if (!passwordResult.isValid) {
      errors.push(passwordResult.error!);
    }

    const firstNameResult = validateName(data.firstName || '', 'First name');
    if (!firstNameResult.isValid) {
      errors.push(firstNameResult.error!);
    }

    const lastNameResult = validateName(data.lastName || '', 'Last name');
    if (!lastNameResult.isValid) {
      errors.push(lastNameResult.error!);
    }

    if (errors.length > 0) {
      return { isValid: false, error: errors.join(', ') };
    }

    return { isValid: true };
  }, [validateEmail, validatePassword, validateName]);

  // Profile step validation (Step 2)
  const validateProfileStep = useCallback((data: Partial<PatientRegistrationData>): ValidationResult => {
    const errors: string[] = [];

    if (data.address) {
      const addressResult = validateAddress(data.address);
      if (!addressResult.isValid) {
        errors.push(addressResult.error!);
      }
    } else {
      errors.push('Address is required');
    }

    if (data.phone) {
      const phoneResult = validatePhone(data.phone);
      if (!phoneResult.isValid) {
        errors.push(phoneResult.error!);
      }
    } else {
      errors.push('Phone number is required');
    }

    const dateOfBirthResult = validateDateOfBirth(data.date_of_birth || '');
    if (!dateOfBirthResult.isValid) {
      errors.push(dateOfBirthResult.error!);
    }

    // Photo is optional, but validate if provided
    if (data.photo) {
      const photoResult = validatePhoto(data.photo);
      if (!photoResult.isValid) {
        errors.push(photoResult.error!);
      }
    }

    if (errors.length > 0) {
      return { isValid: false, error: errors.join(', ') };
    }

    return { isValid: true };
  }, [validateAddress, validatePhone, validateDateOfBirth, validatePhoto]);

  // Step validation
  const validateStep = useCallback((step: number, formData: Partial<PatientRegistrationData>): ValidationResult => {
    switch (step) {
      case 1:
        return validateRegistrationStep(formData);
      case 2:
        return validateProfileStep(formData);
      default:
        return { isValid: false, error: 'Invalid step' };
    }
  }, [validateRegistrationStep, validateProfileStep]);

  return {
    validateEmail,
    validatePassword,
    validateName,
    validateAddress,
    validateStreet,
    validateCity,
    validateState,
    validatePostalCode,
    validatePhone,
    validateDateOfBirth,
    validatePhoto,
    validateStep
  };
}